import { supabase } from '@/integrations/supabase/client';

import { createCRUDEntity } from './crud-factory';

// Application-level TreatmentEquipment type (transformed from DB)
export type TreatmentEquipmentType = {
  id: string;
  treatmentId: string;
  equipmentId: string;
  priority: number | null;
  createdAt: string | null;
};

const transformFromDB = (data: unknown): TreatmentEquipmentType => {
  if (!data) {
    throw new Error('Cannot transform null/undefined data');
  }
  const dataRecord = data as Record<string, unknown>;

  // Helper function to safely convert values to strings
  const safeString = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return ''; // Return empty string for objects/arrays/functions
  };

  return {
    id: safeString(dataRecord.id),
    treatmentId: safeString(dataRecord.treatment_id),
    equipmentId: safeString(dataRecord.equipment_id),
    priority: dataRecord.priority as number | null,
    createdAt: dataRecord.created_at as string | null,
  };
};

const transformToDB = (data: unknown) => {
  const dataRecord = data as Record<string, unknown>;
  return {
    treatment_id: dataRecord.treatmentId,
    equipment_id: dataRecord.equipmentId,
    priority: dataRecord.priority,
  };
};

const transformConfig = {
  transformFromDB,
  transformToDB,
};

export const TreatmentEquipment = createCRUDEntity<'treatment_equipment', TreatmentEquipmentType>(
  'treatment_equipment',
  transformConfig,
  {
    customMethods: {
      // Custom method to get equipment for a treatment with joined data
      getByTreatment: async (treatmentId: string) => {
        const { data, error } = await supabase
          .from('treatment_equipment')
          .select(
            `
            *,
            equipment:equipment_id (*)
          `
          )
          .eq('treatment_id', treatmentId)
          .order('priority', { ascending: true });

        if (error) throw error;

        return data.map(item => ({
          ...(transformFromDB(item) as Record<string, unknown>),
          equipment: {
            ...item.equipment,
            regulatoryApprovals: item.equipment.regulatory_approvals ?? [],
            galleryImages: item.equipment.gallery_images ?? [],
            globalLaunchYear: item.equipment.global_launch_year,
            mainPhotoUrl: (item.equipment as Record<string, unknown>).main_photo_url,
            altTextMainPhoto: (item.equipment as Record<string, unknown>).alt_text_main_photo,
            shortTagline: item.equipment.short_tagline,
            technologyType: item.equipment.technology_type,
            keyFeatures: item.equipment.key_features,
          },
        }));
      },
    },
  }
);
export default TreatmentEquipment;
